import { useId } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import { getUsagePercentage, SUPPORT_EMAIL } from '../../../utils/constants/subscription';

/**
 * One counted balance in the agent sidebar usage panel — contact credits or
 * company downloads (TD-2883). Company Tokens keeps its own component: it is
 * shown as a percentage and carries the fallback / on-hold modals.
 *
 * The bar fills with what is LEFT, matching the token meter above it, so a
 * short bar always means the same thing across the panel.
 *
 * Colour comes from the caller (each balance keeps the thresholds it already
 * uses elsewhere) except when the balance is spent, which greys the meter and
 * offers a sales contact — the on-hold treatment tokens already use.
 *
 * A zero allowance (`limit === 0`, e.g. a demo contract with no exports) is a
 * counter reading 0, not a hidden meter: the user should see that the balance
 * exists and is empty. Note that `isLimitReached` returns false for limit 0, so
 * exhaustion is computed here rather than taken from the hook.
 */
const AgentUsageMeter = ({
  label,
  infoText,
  used,
  limit,
  remaining,
  color,
  limitReached = false,
  collapsed = false
}) => {
  const anchorId = useId();
  // React 18+ useId() returns colon-bracketed ids like ":r0:" — the CSS selector
  // react-tooltip's anchorSelect takes needs them escaped.
  const anchorSelector = `#${CSS.escape(anchorId)}`;

  const exhausted = limitReached || limit === 0;
  // getUsagePercentage returns 0 for an unusable limit, which would fill the bar
  // completely for an allowance of zero — the opposite of what is left.
  const percentRemaining = limit > 0 ? Math.max(0, 100 - getUsagePercentage(used, limit)) : 0;
  // Compact in the rail ("868/2,500" — unspaced, so a four-digit pair still
  // leaves the label a single line), spelled out for screen readers and for the
  // collapsed dot's tooltip, where there is no label beside it.
  const valueText = `${remaining.toLocaleString()}/${limit.toLocaleString()}`;
  const fullText = `${remaining.toLocaleString()} of ${limit.toLocaleString()} left`;
  const barColor = exhausted ? 'grey' : color;

  if (collapsed) {
    return (
      <div className="agent-usage-dot-wrap" title={`${label} — ${fullText}`}>
        <div className={`agent-token-dot agent-token-dot-${barColor}`} />
      </div>
    );
  }

  return (
    <div className={`agent-usage-meter${exhausted ? ' agent-usage-meter-on-hold' : ''}`}>
      <div className="company-tokens-header">
        <span className={`company-tokens-label${exhausted ? ' company-tokens-label-onhold' : ''}`}>
          {label}
        </span>
        <span
          id={anchorId}
          className="default-black-info-circle-wrap company-tokens-info-icon"
          aria-label={`${label} details`}
        >
          <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} alt="" />
        </span>
        <Tooltip
          anchorSelect={anchorSelector}
          variant="dark"
          place="bottom-center"
          opacity={1}
          multiline
          content={infoText}
        />
        <span className="agent-usage-meter-value">{valueText}</span>
      </div>
      {/*
        role="progressbar" rather than <progress> — same deliberate choice as
        UsageProgressBar, which this markup mirrors so both meters share the
        colour classes. See the comment there for the rationale.
      */}
      <div
        className="usage-progress-track"
        role="progressbar"
        aria-valuenow={percentRemaining}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        aria-valuetext={fullText}
      >
        <div
          className={`usage-progress-fill usage-${barColor}`}
          style={{ width: `${percentRemaining}%` }}
        />
      </div>
      {exhausted && (
        <span className="onhold-subline">
          On hold ·{' '}
          <a
            className="onhold-subline-link"
            href={`mailto:${SUPPORT_EMAIL}`}
            aria-label={`Email sales at ${SUPPORT_EMAIL}`}
          >
            contact sales
          </a>
        </span>
      )}
    </div>
  );
};

AgentUsageMeter.propTypes = {
  label: PropTypes.string.isRequired,
  infoText: PropTypes.string.isRequired,
  used: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  remaining: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  limitReached: PropTypes.bool,
  collapsed: PropTypes.bool
};

export default AgentUsageMeter;
